import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Plus, Search as SearchIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Kicker } from "@/components/ui/kicker";
import { Skeleton } from "@/components/ui/skeleton";
import { QuickStartBanner } from "@/components/experience/quick-start-banner";
import { useCanEdit } from "@/lib/project-access";
import { cn } from "@/lib/utils";

interface ProjectActionRailProps {
  projectId: string;
  openProblems: number;
  triageCount: number;
  fixNextCount: number;
  awaitingRerunCount: number;
  scenarioCount?: number;
  isLoading?: boolean;
}

export function ProjectActionRail({
  projectId,
  openProblems,
  triageCount,
  fixNextCount,
  awaitingRerunCount,
  scenarioCount = 0,
  isLoading = false,
}: ProjectActionRailProps) {
  const canEdit = useCanEdit();

  // Primary action drives the next logical step in the findings lifecycle
  const primaryAction = useMemo(() => {
    if (triageCount > 0) {
      return {
        label: `Review new findings (${triageCount})`,
        href: `/projects/${projectId}/findings?view=triage`,
      };
    }
    if (fixNextCount > 0) {
      return {
        label: `View ready to fix (${fixNextCount})`,
        href: `/projects/${projectId}/findings?view=fix_next`,
      };
    }
    if (awaitingRerunCount > 0) {
      return {
        label: `View awaiting re-run (${awaitingRerunCount})`,
        href: `/projects/${projectId}/findings?view=awaiting_rerun`,
      };
    }
    if (openProblems > 0) {
      return {
        label: `View all findings (${openProblems})`,
        href: `/projects/${projectId}/findings`,
      };
    }
    return null;
  }, [projectId, triageCount, fixNextCount, awaitingRerunCount, openProblems]);

  const totalActive = triageCount + fixNextCount + awaitingRerunCount;

  return (
    <div className="rounded-lg border border-border/80 bg-card p-5 space-y-4 shadow-xs dark:shadow-[inset_0_1px_0_0_oklch(1_0_0_/_0.06),0_4px_20px_-4px_oklch(0_0_0_/_0.4)]">
      <div className="flex items-center justify-between border-b border-border/60 pb-3">
        <Kicker as="h3">Work queue</Kicker>
        {isLoading ? (
          <Skeleton className="h-4 w-16" />
        ) : totalActive > 0 ? (
          <span className="text-xs font-semibold tabular-nums text-severity-important">
            {openProblems > 0
              ? `${openProblems} open`
              : `${totalActive} active`}
          </span>
        ) : (
          <span className="text-xs font-semibold tabular-nums text-finding-helps">
            Queue clear
          </span>
        )}
      </div>

      {/* Primary Action Button */}
      {canEdit && (
        <div className="space-y-2">
          {isLoading ? (
            <Skeleton className="h-9 w-full rounded-sm" />
          ) : primaryAction ? (
            <Button asChild className="w-full" size="default">
              <Link to={primaryAction.href}>
                <SearchIcon className="mr-2 h-4 w-4" />
                {primaryAction.label}
              </Link>
            </Button>
          ) : scenarioCount > 0 ? (
            <>
              <QuickStartBanner projectId={projectId} asButton />
              <Button asChild variant="outline" className="w-full" size="sm">
                <Link to={`/projects/${projectId}/scenarios`}>
                  <Plus className="mr-2 h-3.5 w-3.5" />
                  New scenario
                </Link>
              </Button>
            </>
          ) : (
            <Button asChild className="w-full" size="default">
              <Link to={`/projects/${projectId}/scenarios`}>
                <Plus className="mr-2 h-4 w-4" />
                New scenario
              </Link>
            </Button>
          )}

          {/* Secondary Action: Direct scenario runner modal when primaryAction is present */}
          {!isLoading && primaryAction && (
            <div className="pt-0.5">
              <QuickStartBanner projectId={projectId} asButton />
            </div>
          )}
        </div>
      )}

      {/* Lifecycle Queue Breakdown */}
      <div className="border-t border-border/60 pt-3">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
          <span>Lifecycle stage</span>
          <span>Count</span>
        </div>
        <div className="space-y-0.5">
          <Link
            to={`/projects/${projectId}/findings?view=triage`}
            className="flex items-center justify-between rounded-md px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground group"
          >
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full shrink-0",
                  !isLoading && triageCount > 0
                    ? "bg-amber-500"
                    : "bg-muted-foreground/30",
                )}
              />
              <span className="font-medium group-hover:text-foreground">
                Unreviewed
              </span>
            </div>
            {isLoading ? (
              <Skeleton className="h-3.5 w-5" />
            ) : (
              <span
                className={cn(
                  "tabular-nums text-xs font-semibold",
                  triageCount > 0 ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {triageCount}
              </span>
            )}
          </Link>

          <Link
            to={`/projects/${projectId}/findings?view=fix_next`}
            className="flex items-center justify-between rounded-md px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground group"
          >
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full shrink-0",
                  !isLoading && fixNextCount > 0
                    ? "bg-severity-important"
                    : "bg-muted-foreground/30",
                )}
              />
              <span className="font-medium group-hover:text-foreground">
                Ready to fix
              </span>
            </div>
            {isLoading ? (
              <Skeleton className="h-3.5 w-5" />
            ) : (
              <span
                className={cn(
                  "tabular-nums text-xs font-semibold",
                  fixNextCount > 0
                    ? "text-foreground"
                    : "text-muted-foreground",
                )}
              >
                {fixNextCount}
              </span>
            )}
          </Link>

          <Link
            to={`/projects/${projectId}/findings?view=awaiting_rerun`}
            className="flex items-center justify-between rounded-md px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground group"
          >
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full shrink-0",
                  !isLoading && awaitingRerunCount > 0
                    ? "bg-sky-500"
                    : "bg-muted-foreground/30",
                )}
              />
              <span className="font-medium group-hover:text-foreground">
                Awaiting re-run
              </span>
            </div>
            {isLoading ? (
              <Skeleton className="h-3.5 w-5" />
            ) : (
              <span
                className={cn(
                  "tabular-nums text-xs font-semibold",
                  awaitingRerunCount > 0
                    ? "text-foreground"
                    : "text-muted-foreground",
                )}
              >
                {awaitingRerunCount}
              </span>
            )}
          </Link>
        </div>
      </div>
    </div>
  );
}
